/**
 * useNotifications Hook
 * 
 * Unified notification hook that integrates with the Communications Hub.
 * Provides inbox management, preferences, and real-time updates.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePlatformTenant } from '../contexts/PlatformTenantContext';
import { 
  CommunicationsHubService, 
  type InboxItem, 
  type Channel 
} from '../services/communicationsHubService';
import { supabase } from '../integrations/supabase/client';

export interface NotificationPreference {
  channel: Channel;
  deliveryMode: 'immediate' | 'digest' | 'scheduled' | 'muted';
  digestFrequency?: 'hourly' | 'daily' | 'weekly';
  isEnabled: boolean;
  quietHours?: {
    enabled: boolean;
    startTime: string;
    endTime: string;
    timezone: string;
    daysOfWeek: number[];
  };
}

interface UseNotificationsOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
  unreadOnly?: boolean;
  limit?: number;
}

interface UseNotificationsReturn {
  // Inbox
  notifications: InboxItem[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  
  // Actions
  markAsRead: (itemId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  archiveNotification: (itemId: number) => Promise<void>;
  acknowledgeNotification: (itemId: number) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  
  // Preferences
  preferences: NotificationPreference[];
  preferencesLoading: boolean;
  updateChannelPreference: (channel: Channel, settings: Partial<NotificationPreference>) => Promise<void>;
  
  // Hub status
  hubEnabled: boolean;
  hubInitialized: boolean;
}

export const useNotifications = (options: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const { 
    autoRefresh = true, 
    refreshInterval = 60000, 
    unreadOnly = false,
    limit = 20 
  } = options;
  
  const { user } = useAuth();
  const { platformTenant } = usePlatformTenant();
  
  const [notifications, setNotifications] = useState<InboxItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  
  const [hubEnabled, setHubEnabled] = useState(false);
  const [hubInitialized, setHubInitialized] = useState(false);

  // Use platformTenant data directly instead of re-querying (Phase 2B optimization)
  useEffect(() => {
    if (!platformTenant) return;
    
    // Access hub_enabled from platformTenant context directly
    // The select('*') in PlatformTenantContext already fetches this field
    const hubEnabledFromTenant = (platformTenant as any).communications_hub_enabled ?? false;
    setHubEnabled(hubEnabledFromTenant);
  }, [platformTenant]);

  // Lazy Hub initialization - only init when actually loading notifications
  const initHubOnDemand = useCallback(async () => {
    if (!hubEnabled || hubInitialized || !platformTenant?.id) return false;
    
    try {
      await CommunicationsHubService.initialize(platformTenant.id);
      setHubInitialized(true);
      return true;
    } catch (err) {
      console.error('[useNotifications] Hub init error:', err);
      return false;
    }
  }, [hubEnabled, hubInitialized, platformTenant?.id]);

  // Load from local Supabase (merges onboarding_user_notifications + in_app_notifications)
  const loadFromLocal = useCallback(async (userId: string, userEmail: string) => {
    // Fetch from legacy onboarding_user_notifications
    const { data: legacyData, error: legacyErr } = await supabase
      .from('onboarding_user_notifications')
      .select('*')
      .eq('user_email', userEmail)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (legacyErr) console.warn('[useNotifications] Legacy notifications error:', legacyErr);
    
    const legacyMapped: InboxItem[] = (legacyData || []).map((n: any) => ({
      id: n.id,
      subjectType: 'user' as const,
      subjectRef: userId,
      title: n.title,
      body: n.message,
      severity: getSeverityFromType(n.notification_type),
      contextType: n.context_type,
      contextId: n.context_id,
      actionUrl: n.action_url,
      actions: n.action_url ? [{
        label: n.action_label || 'View',
        url: n.action_url,
        type: 'primary' as const
      }] : undefined,
      readAt: n.is_read ? n.updated_at : undefined,
      createdAt: n.created_at,
      _source: 'legacy' as const,
    }));

    // Fetch from new in_app_notifications (hub-dispatched)
    const { data: hubData, error: hubErr } = await supabase
      .from('in_app_notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (hubErr) console.warn('[useNotifications] In-app notifications error:', hubErr);

    const hubMapped: InboxItem[] = (hubData || []).map((n: any) => ({
      id: n.id,
      subjectType: 'user' as const,
      subjectRef: userId,
      title: n.title,
      body: n.body,
      severity: mapPriorityToSeverity(n.priority),
      contextType: n.category,
      actionUrl: n.action_url,
      actions: n.action_url ? [{
        label: n.action_label || 'View',
        url: n.action_url,
        type: 'primary' as const
      }] : undefined,
      readAt: n.is_read ? n.read_at : undefined,
      createdAt: n.created_at,
      _source: 'in_app' as const,
    }));

    // Merge and sort by created_at desc, then take top `limit`
    const merged = [...legacyMapped, ...hubMapped]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, limit);
    
    setNotifications(merged);
    setUnreadCount(merged.filter(n => !n.readAt).length);
  }, [limit]);

  // Map in_app_notifications priority to severity
  const mapPriorityToSeverity = (priority: string): 'info' | 'success' | 'warning' | 'error' => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'low': return 'info';
      default: return 'info';
    }
  };

  // Load notifications from Hub or fallback to local
  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Lazy init Hub on first notification load if enabled
      if (hubEnabled && !hubInitialized) {
        await initHubOnDemand();
      }
      
      if (hubEnabled && hubInitialized) {
        // Try Communications Hub inbox first
        try {
          const response = await CommunicationsHubService.getInbox(
            'user',
            user.id,
            { unreadOnly, limit }
          );
          
          if (response.success && response.data) {
            setNotifications(response.data.items);
            setUnreadCount(response.data.unreadCount);
            return; // Success - exit early
          }
        } catch (hubError: any) {
          // If Hub fails (e.g., invalid API key), fall back to local silently
          console.warn('[useNotifications] Hub failed, falling back to local:', hubError.code || hubError.message);
        }
      }
      
      // Fallback to local Supabase (either Hub disabled or Hub failed)
      await loadFromLocal(user.id, user.email || '');
    } catch (err) {
      console.error('[useNotifications] Load error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [user?.id, hubEnabled, hubInitialized, unreadOnly, limit, loadFromLocal, initHubOnDemand]);

  // Helper to convert notification type to severity
  const getSeverityFromType = (type: string): 'info' | 'success' | 'warning' | 'error' => {
    if (type.includes('completed') || type.includes('confirmed') || type.includes('accepted')) {
      return 'success';
    }
    if (type.includes('declined') || type.includes('failed') || type.includes('error')) {
      return 'error';
    }
    if (type.includes('reminder') || type.includes('warning') || type.includes('pending')) {
      return 'warning';
    }
    return 'info';
  };

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(loadNotifications, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadNotifications]);

  // Real-time subscription for local fallback
  useEffect(() => {
    if (!user?.id || (hubEnabled && hubInitialized)) return;
    
    const channel = supabase
      .channel('user-notifications-hook')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const n = payload.new as any;
          const newItem: InboxItem = {
            id: n.id,
            subjectType: 'user',
            subjectRef: user.id,
            title: n.title,
            body: n.message,
            severity: getSeverityFromType(n.notification_type),
            contextType: n.context_type,
            contextId: n.context_id,
            actionUrl: n.action_url,
            actions: n.action_url ? [{
              label: n.action_label || 'View',
              url: n.action_url,
              type: 'primary'
            }] : undefined,
            createdAt: n.created_at
          };
          
          setNotifications(prev => [newItem, ...prev]);
          setUnreadCount(prev => prev + 1);
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, hubEnabled, hubInitialized]);

  // Mark as read
  const markAsRead = useCallback(async (itemId: number) => {
    try {
      if (hubEnabled && hubInitialized) {
        await CommunicationsHubService.markInboxItemRead(itemId);
      } else {
        // Determine source from current notifications state
        const notif = notifications.find(n => n.id === itemId);
        if ((notif as any)?._source === 'in_app') {
          await supabase
            .from('in_app_notifications')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('id', itemId);
        } else {
          await (supabase as any)
            .from('user_notifications')
            .update({ is_read: true })
            .eq('id', itemId);
        }
      }
      
      setNotifications(prev => 
        prev.map(n => n.id === itemId ? { ...n, readAt: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('[useNotifications] Mark read error:', err);
    }
  }, [hubEnabled, hubInitialized, notifications]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      if (hubEnabled && hubInitialized) {
        const unreadIds = notifications.filter(n => !n.readAt).map(n => n.id);
        if (unreadIds.length > 0) {
          await CommunicationsHubService.markInboxItemsRead(unreadIds);
        }
      } else {
        // Mark legacy notifications as read
        await (supabase as any)
          .from('user_notifications')
          .update({ is_read: true })
          .eq('user_id', user.id)
          .eq('is_read', false);

        // Mark in_app_notifications as read
        await supabase
          .from('in_app_notifications')
          .update({ is_read: true, read_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .eq('is_read', false);
      }
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, readAt: n.readAt || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('[useNotifications] Mark all read error:', err);
    }
  }, [user?.id, hubEnabled, hubInitialized, notifications]);

  // Archive notification
  const archiveNotification = useCallback(async (itemId: number) => {
    try {
      if (hubEnabled && hubInitialized) {
        await CommunicationsHubService.archiveInboxItem(itemId);
      }
      
      setNotifications(prev => prev.filter(n => n.id !== itemId));
      
      const wasUnread = notifications.find(n => n.id === itemId && !n.readAt);
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('[useNotifications] Archive error:', err);
    }
  }, [hubEnabled, hubInitialized, notifications]);

  // Acknowledge notification
  const acknowledgeNotification = useCallback(async (itemId: number) => {
    try {
      if (hubEnabled && hubInitialized) {
        await CommunicationsHubService.acknowledgeInboxItem(itemId);
      }
      
      setNotifications(prev => 
        prev.map(n => n.id === itemId 
          ? { ...n, acknowledgedAt: new Date().toISOString(), readAt: n.readAt || new Date().toISOString() } 
          : n
        )
      );
    } catch (err) {
      console.error('[useNotifications] Acknowledge error:', err);
    }
  }, [hubEnabled, hubInitialized]);

  // Load preferences
  const loadPreferences = useCallback(async () => {
    if (!user?.id || !hubEnabled || !hubInitialized) return;
    
    setPreferencesLoading(true);
    try {
      const response = await CommunicationsHubService.getPreferences('user', user.id);
      if (response.success && response.data) {
        setPreferences(response.data as NotificationPreference[]);
      }
    } catch (err) {
      console.error('[useNotifications] Preferences load error:', err);
    } finally {
      setPreferencesLoading(false);
    }
  }, [user?.id, hubEnabled, hubInitialized]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Update channel preference
  const updateChannelPreference = useCallback(async (
    channel: Channel, 
    settings: Partial<NotificationPreference>
  ) => {
    if (!user?.id || !hubEnabled || !hubInitialized) return;
    
    try {
      await CommunicationsHubService.updateChannelPreference('user', user.id, channel, {
        deliveryMode: settings.deliveryMode,
        digestFrequency: settings.digestFrequency,
        isEnabled: settings.isEnabled
      });
      
      // Update local state
      setPreferences(prev => 
        prev.map(p => p.channel === channel ? { ...p, ...settings } : p)
      );
    } catch (err) {
      console.error('[useNotifications] Update preference error:', err);
      throw err;
    }
  }, [user?.id, hubEnabled, hubInitialized]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    acknowledgeNotification,
    refreshNotifications: loadNotifications,
    preferences,
    preferencesLoading,
    updateChannelPreference,
    hubEnabled,
    hubInitialized
  };
};

export default useNotifications;
